'use client';

import Image from 'next/image';
import { DropdownMenu, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { Link } from '@/app/[lang]/(main)/components/shared/PrivateLink/ServerLink';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { useIsLoading, useIsLogined, useLogout, useOpenLoginModal, useUserData } from '@/contexts/LoginContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

export const UserControl = () => {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);

  const isLogined = useIsLogined();
  const isLoading = useIsLoading();
  const userData = useUserData();
  const logout = useLogout();

  const onClickLogout = useCallback(() => {
    logout();
    window.location.reload();
  }, [logout]);

  const openLoginModal = useOpenLoginModal();

  const openLoginModalCB = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('login', 'true');
    history.pushState({}, '', url);
    openLoginModal();
  }, [openLoginModal]);

  if (isLoading) {
    return <Skeleton className='lg:w-[155px] w-[24px] h-[24px]' />;
  }

  return (
    <>
      <DropdownMenu modal={false} open={isOpen} onOpenChange={setIsOpen}>
        {isLogined && userData ? (
          <Link
            href='/my'
            className='lg:bg-[#f3f5f9] lg:rounded-full lg:ps-[4px] lg:pe-[10px] lg:py-[3px] items-center border-0 flex gap-[6px] cursor-pointer border-none! focus:outline-hidden focus:ring-0 focus:shadow-none'
          >
            {userData && (
              <Avatar className='size-[26px]'>
                <AvatarImage src={userData?.photoUrl ?? undefined} alt='' />
                <AvatarFallback className='bg-primary-green-day text-white'>
                  {userData?.firstName?.charAt(0) || userData.email?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            )}

            <div className='text-m lg:inline-block hidden'>{t('controls.header.profile')}</div>
          </Link>
        ) : (
          <Button
            variant='ghost'
            onClick={openLoginModalCB}
            title={t('alts.login')}
            className='lg:h-[24px] lg:min-w-[100px] h-[26px] bg-transparent hover:bg-transparent px-0 border-0 border-none! focus:outline-hidden focus:ring-0 focus:shadow-none'
          >
            <Image width={24} height={24} src='/assets/icons/user-login.svg' alt={t('alts.login')} className='' />

            <div className='text-m hidden lg:block'>{t('login.login_button')}</div>
          </Button>
        )}

        <DropdownMenuContent
          sticky={'always'}
          sideOffset={8}
          className='z-60 max-w-md w-full p-[8px] max-h-[300px] shadow-[5px_5px_22px_10px_rgba(40,65,155,0.16)]'
        >
          <div className='flex flex-col gap-[4px]'>
            <Link href={'/my/products'}>
              <div
                onClick={() => {
                  setIsOpen(false);
                }}
                className='flex gap-[8px] border-b border-solid border-primary-stroke-day py-[10px] min-w-[204px] items-center'
              >
                <Image
                  width={24}
                  height={24}
                  src={`/assets/icons/type=cards.svg`}
                  alt='globe'
                  className='w-[24px] h-[24px]'
                />
                <div className='text-m'>{t('controls.header.my_products')}</div>
              </div>
            </Link>

            <Link href={'/my/favorites'}>
              <div
                onClick={() => {
                  setIsOpen(false);
                }}
                className='flex gap-[8px] border-b border-solid border-primary-stroke-day py-[10px] min-w-[204px] items-center'
              >
                <Image
                  width={24}
                  height={24}
                  src={`/assets/icons/my_favorites.svg`}
                  alt='globe'
                  className='w-[24px] h-[24px]'
                />
                <div className='text-m'>{t('controls.header.favorites')}</div>
              </div>
            </Link>

            <Link href={'/'}>
              <div
                onClick={() => {
                  setIsOpen(false);
                }}
                className='flex gap-[8px] border-b border-solid border-primary-stroke-day py-[10px] min-w-[204px] items-center'
              >
                <Image
                  width={24}
                  height={24}
                  src={`/assets/icons/settings.svg`}
                  alt='globe'
                  className='w-[24px] h-[24px]'
                />
                <div className='text-m'>{t('controls.header.settings')}</div>
              </div>
            </Link>

            <div
              className='flex gap-[8px] border-b border-solid border-primary-stroke-day cursor-pointer  py-[10px] min-w-[204px] items-center'
              onClick={onClickLogout}
            >
              <Image
                width={24}
                height={24}
                src={`/assets/icons/logout.svg`}
                alt='globe'
                className='w-[24px] h-[24px]'
              />
              <div className='text-m'>{t('controls.header.logout')}</div>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
